import { DEFAULT_SELECT_PAGE_SIZE, PURPOSE_CODE_Z } from '@common/constants';
import { FormSelectAsyncPaginate } from '@common/FormSelectAsyncPaginate';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { INoteItem, NoteList } from '@core/Note/NoteList';
import { ComponentType, FormFieldMethods, FormSelect, Grid, Icon, requiredRule, UseFormReturn } from '@gcl/react-core';
import { ETransmittalType } from '@transmittal/transmittal.constants';

import { IProjectTransmittalDetail, IProjectTransmittalDistribution } from '@transmittal/transmittal.models';
import { ITransmittalPurpose } from '@transmittal/transmittalPurpose/transmittalPurpose.models';
import { useFetchRseOptionsWithSearch } from '@user/user.queries';
import * as React from 'react';
import { TransmittalDetailChangeForT3 } from './TransmittalDetailChangeForT3';
import { TransmittalDetailDistribution } from './TransmittalDetailDistribution';
import { TransmittalDetailEndorsement } from './TransmittalDetailEndorsement';
import { ETransmittalDetailsFormKeys } from './transmittalDetailPage.constants';
import {
  TransmittalDetailTabDetailTopBar,
  ITransmittalDetailTabDetailTopBarProps,
} from './TransmittalDetailTabDetailTopBar';
import './TransmittalDetailTabDetailTopBar.scss';
import { IUserProfileOption } from '@userProfile/userProfile.models';
import { IProjectDesignModificationOptionForDesignerTransmittal } from '@designModification/designModification.models';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import { TransmittalItemTableForm } from './transmittalItemTableForm/TransmittalItemTableForm';
import { ITransmittalItemTableEditingRef } from './transmittalItemTableForm/transmittalItemTableForm.models';
import { ITransmittalDetailsFormState } from './transmittalDetailPage.models';

interface ITransmittalDetailTabDetailProps extends ITransmittalDetailTabDetailTopBarProps {
  useFormMethods: UseFormReturn;
  isReadOnly: boolean;
  transmittalDetails?: IProjectTransmittalDetail | null;
  transmittalItemDocumentTypes: IProjectTransmittalItemDocumentType[];
  projectId: number;
  notes: INoteItem<React.ReactElement<typeof Icon>>[];
  transmittalOptions: ITransmittalPurpose[];
  shouldShowRemovedFromUse: boolean;
  distributionData?: IProjectTransmittalDistribution;
  formFieldMethods: FormFieldMethods;
  designModificationOptions?: IProjectDesignModificationOptionForDesignerTransmittal[];
  isAnyActionLoading: boolean;
  setIsAnyActionLoading: (isLoading: boolean) => void;
  setRowTableIsEditing: (index: number, isEditing: boolean) => void;
  setRowTableIsUploading: (index: number, isUploading: boolean) => void;
  setTableIsIceDesignCertified: (index: number, isIceDesignCertified: boolean) => void;
  isPageDirty: boolean;
  hasDeletePermission: boolean;
  transmittalItemTableEditingRef: React.MutableRefObject<ITransmittalItemTableEditingRef>;
  isWindowFocus: boolean;
  setDefaultValues: (value: React.SetStateAction<ITransmittalDetailsFormState>) => void;
}
export const TransmittalDetailTabDetail: React.FunctionComponent<ITransmittalDetailTabDetailProps> = ({
  renderExportExcelButton,
  renderExportPdfButton,
  onSearch,
  onClickShowHideRemovedItems,
  isReadOnly,
  useFormMethods,
  transmittalDetails,
  transmittalItemDocumentTypes,
  projectId,
  notes,
  transmittalOptions,
  shouldShowRemovedFromUse,
  distributionData,
  formFieldMethods,
  designModificationOptions,
  isAnyActionLoading,
  setIsAnyActionLoading,
  setRowTableIsEditing,
  setRowTableIsUploading,
  setTableIsIceDesignCertified,
  isPageDirty,
  hasDeletePermission,
  transmittalItemTableEditingRef,
  isWindowFocus,
  setDefaultValues,
}) => {
  const purposeCodeSelected = useFormMethods.watch(ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE);

  const isZCode =
    purposeCodeSelected &&
    ((Array.isArray(purposeCodeSelected) &&
      (purposeCodeSelected as ITransmittalPurpose[])?.find((pcs) => pcs.code === PURPOSE_CODE_Z)) ||
      (purposeCodeSelected as ITransmittalPurpose).code === PURPOSE_CODE_Z);

  const signatureLabel = React.useMemo(() => {
    if (!transmittalDetails) return undefined;

    switch (transmittalDetails.type) {
      case ETransmittalType.DESIGNER:
        return "Issued By Designer's Authorised Representative";
      case ETransmittalType.REVIEWER:
      case ETransmittalType.SITE:
        return 'Issued By Project Manager / TWC';
      case ETransmittalType.ICE:
        return 'Issued By Independent Checking Engineer';
      default:
        return undefined;
    }
  }, [transmittalDetails?.type]);

  return (
    <div className="gcl-mt-md">
      <TransmittalDetailTabDetailTopBar
        isPageDirty={isPageDirty}
        transmittalId={transmittalDetails?.id}
        transmittalStatus={transmittalDetails?.status}
        transmittalType={transmittalDetails?.type}
        masterScheduleId={transmittalDetails?.masterScheduleId || 0}
        renderExportExcelButton={renderExportExcelButton}
        renderExportPdfButton={renderExportPdfButton}
        onSearch={onSearch}
        onClickShowHideRemovedItems={onClickShowHideRemovedItems}
        shouldShowRemovedItems={shouldShowRemovedFromUse}
        isAnyActionLoading={isAnyActionLoading}
        isReadOnly={isReadOnly}
      />

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12} md={12}>
          <LabelWithMessage
            text="Purpose for issue"
            labelFor={ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE}
            message={
              isZCode
                ? 'Purpose code is set to Z - Approved by Authorities. TWC could Release for Construction without further checking process.'
                : undefined
            }
            alwaysOn
            iconWithColor
            componentType={ComponentType.Warning}
            iconName="ExclamationTriangle"
          />
          <FormSelect
            disabled={isReadOnly}
            options={transmittalOptions}
            formFieldMethods={formFieldMethods}
            id={ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE}
            rules={requiredRule}
            dropUp
            allowSearch={false}
            clearable={false}
            displayFieldName="displayName"
          />
        </Grid>
        {isZCode ? (
          <Grid item xs={12} md={12}>
            <LabelWithMessage text="RSE/RGE" labelFor={ETransmittalDetailsFormKeys.RSE} />
            <FormSelectAsyncPaginate
              shouldDebounce
              pageSize={DEFAULT_SELECT_PAGE_SIZE}
              rules={requiredRule}
              displayFieldName="displayName"
              disabled={isReadOnly}
              formFieldMethods={formFieldMethods}
              useFetchOptions={useFetchRseOptionsWithSearch({ shouldConvertOptions: false })}
              useFetchOptionsProps={{ projectId }}
              id={ETransmittalDetailsFormKeys.RSE}
              value={formFieldMethods.getValues(ETransmittalDetailsFormKeys.RSE) ?? []}
              transform={{
                output: (value) => {
                  if (value === undefined || value === null || (Array.isArray(value) && value.length <= 0))
                    return undefined;
                  const tempValue: IUserProfileOption = Array.isArray(value) ? value[0] : value;
                  return [
                    {
                      id: tempValue.id ?? 0,
                      displayName: tempValue.displayName,
                      uid: tempValue.uid,
                      email: tempValue.email,
                      upn: tempValue.upn,
                    },
                  ];
                },
                input: (value) => {
                  if (value === undefined || value === null) return undefined;
                  return !Array.isArray(value) ? [value] : value;
                },
              }}
            />
          </Grid>
        ) : undefined}

        <Grid item xs={12}>
          <NoteList items={notes} />
        </Grid>
        {transmittalItemDocumentTypes?.map((transmittalItemDocumentType, index) => {
          return (
            <Grid item xs={12} key={transmittalItemDocumentType.id}>
              <TransmittalItemTableForm
                setDefaultValues={setDefaultValues}
                isWindowFocus={isWindowFocus}
                transmittalItemTableEditingRef={transmittalItemTableEditingRef}
                useFormMethods={useFormMethods}
                tableIndex={index}
                setRowTableIsEditing={setRowTableIsEditing}
                setRowTableIsUploading={setRowTableIsUploading}
                setTableIsIceDesignCertified={setTableIsIceDesignCertified}
                isAnyActionLoading={isAnyActionLoading}
                setIsAnyActionLoading={setIsAnyActionLoading}
                shouldShowRemovedFromUse={shouldShowRemovedFromUse}
                transmittalId={transmittalDetails?.id || 0}
                docType={transmittalItemDocumentType}
                isReadOnly={isReadOnly}
                hasDeletePermission={hasDeletePermission}
              />
            </Grid>
          );
        })}

        {!transmittalDetails ||
          transmittalDetails.id <= 0 ||
          (transmittalDetails.type === ETransmittalType.DESIGNER && (
            <Grid item xs={12}>
              <TransmittalDetailChangeForT3
                formFieldMethods={formFieldMethods}
                isReadOnly={isReadOnly}
                designModificationOptions={designModificationOptions}
                transmittalId={transmittalDetails?.id}
                transmittalStatus={transmittalDetails.status}
                designModifications={transmittalDetails.designModifications}
              />
            </Grid>
          ))}
        <Grid item xs={12}>
          <TransmittalDetailDistribution
            isReadOnly={isReadOnly}
            isAnyActionLoading={isAnyActionLoading}
            masterScheduleId={transmittalDetails?.masterScheduleId}
            transmittalId={transmittalDetails?.id}
            transmittalType={transmittalDetails?.type}
            transmittalStatus={transmittalDetails?.status}
            distributionData={distributionData}
          />
        </Grid>
        <Grid item xs={12}>
          <TransmittalDetailEndorsement
            formFieldMethods={formFieldMethods}
            transmittalDetails={transmittalDetails}
            signatureLabel={signatureLabel}
            isReadOnly={isReadOnly}
          />
        </Grid>
      </Grid>
    </div>
  );
};
